import { useMemo } from 'react';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants';
import { STANDARD_EDITION } from '@atlassian/jira-shared-types';
import { getHasSeenModal } from '../set-modal-local-storage/getHasSeenModal';
import { useExperimentEnrollment } from './useExperimentEnrollment';
import { useProjectType } from './useProjectType';
import { useTrialDetails } from './useTrialDetails';

const isDevtestCohort = (cohort: string): boolean =>
	cohort === 'devtesting' || cohort === 'devtesting_urgent';

export function useExperimentEligibility(experimentName: string): {
	isEligible: boolean;
	isUrgent: boolean;
} {
	const { projectType } = useProjectType();
	const { jswEdition, billingSourceSystem, trialDaysLeft, isInTrial } = useTrialDetails();

	// We include this for our devtesting cohorts. This is not needed for production and should be removed.
	const { cohort } = useExperimentEnrollment({
		experimentName,
		skip: false,
	});

	const isInDevtestCohort = isDevtestCohort(cohort);

	// Note that a user can be urgent even if they are not eligible
	// It only means that they're in the last few days of a trial
	const isUrgent = isInDevtestCohort
		? // We want to consider users in the "devtesting_urgent" cohort urgent but not users in the "devtesting" cohort
			cohort === 'devtesting_urgent'
		: // on day 11 there are 4 days left of the trial so this targets days 11 - 14
			isInTrial && !!trialDaysLeft && trialDaysLeft <= 4;

	// we wrap this in a useMemo to avoid rerendering and recalculating the user as ineligible
	// due to having just seen the modal for the first time
	const hasSeenModal: boolean = useMemo(
		() => getHasSeenModal(experimentName, isUrgent).hasSeenModal,
		[experimentName, isUrgent],
	);

	const isEligible =
		projectType === SOFTWARE_PROJECT &&
		jswEdition === STANDARD_EDITION &&
		billingSourceSystem === 'CCP' &&
		// on day 7 there are 8 days left of the trial so this targets days 7 - 14
		// note that for the devtesting cohort we don't even need to be in a trial to be eligible
		(!isInDevtestCohort ? isInTrial && !!trialDaysLeft && trialDaysLeft <= 8 : true) &&
		!hasSeenModal;

	return { isEligible, isUrgent };
}
